import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setIsMobileMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-charcoal/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-0">
        <div className="flex items-center h-20">
          {/* Logo */}
          <div
            className="cursor-pointer"
            style={{ position: 'fixed', top: '8px', left: '8px' }}
            onClick={() => scrollToSection('hero')}
          >
            <img
              src="/images/logo.jpg"
              alt="Du Sourire à la Fourchette"
              className="h-20 md:h-28 w-auto"
              style={{ borderRadius: '16px' }}
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6 ml-auto pr-8">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-cream hover:text-gold transition-colors duration-200 font-medium"
            >
              Accueil
            </button>
            <button
              onClick={() => scrollToSection('services')}
              className="text-cream hover:text-gold transition-colors duration-200 font-medium"
            >
              Services
            </button>
            <Link
              to="/prestations"
              className={`font-medium transition-colors duration-200 ${
                isActive('/prestations') ? 'text-gold border-b-2 border-gold' : 'text-cream hover:text-gold'
              }`}
            >
              Prestations
            </Link>
            <button
              onClick={() => scrollToSection('gallery')}
              className="text-cream hover:text-gold transition-colors duration-200 font-medium"
            >
              Galerie
            </button>
            <Link
              to="/avis"
              className={`font-medium transition-colors duration-200 ${
                isActive('/avis') ? 'text-gold border-b-2 border-gold' : 'text-cream hover:text-gold'
              }`}
            >
              Avis
            </Link>
            <Link
              to="/blog"
              className={`font-medium transition-colors duration-200 ${
                isActive('/blog') || location.pathname.startsWith('/blog/') ? 'text-gold border-b-2 border-gold' : 'text-cream hover:text-gold'
              }`}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('booking')}
              className="px-5 py-2 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all duration-200 shadow"
            >
              Réserver
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-cream hover:text-gold transition-colors ml-auto pr-4"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-charcoal/98 backdrop-blur-sm border-t border-gold/20">
          <nav className="px-4 py-6 space-y-2">
            {[
              { label: 'Accueil', action: () => scrollToSection('hero') },
              { label: 'Services', action: () => scrollToSection('services') },
              { label: 'Galerie', action: () => scrollToSection('gallery') },
            ].map((item) => (
              <button
                key={item.label}
                onClick={item.action}
                className="block w-full text-left text-cream hover:text-gold transition-colors duration-200 font-medium py-3 border-b border-cream/10"
              >
                {item.label}
              </button>
            ))}
            <Link
              to="/prestations"
              className={`block w-full text-left font-medium py-3 border-b border-cream/10 transition-colors ${isActive('/prestations') ? 'text-gold' : 'text-cream hover:text-gold'}`}
            >
              Prestations
            </Link>
            <Link
              to="/avis"
              className={`block w-full text-left font-medium py-3 border-b border-cream/10 transition-colors ${isActive('/avis') ? 'text-gold' : 'text-cream hover:text-gold'}`}
            >
              Avis
            </Link>
            <Link
              to="/blog"
              className={`block w-full text-left font-medium py-3 border-b border-cream/10 transition-colors ${isActive('/blog') ? 'text-gold' : 'text-cream hover:text-gold'}`}
            >
              Blog
            </Link>
            <button
              onClick={() => scrollToSection('booking')}
              className="block w-full text-left font-medium py-3 text-gold"
            >
              Réserver
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
